/**
 * Real implementation of the investigation-plan data layer.
 *
 * Flow: generatePlan() → opens modal in GENERATING state → polls job status
 * every 3s → on completion fetches the plan document → maps JSON to
 * InvestigationPlan and shows it in the modal → acceptPlan() calls the
 * acceptInvestigationPlan mutation.
 *
 * On mount we do a one-shot job status check so that:
 *  - A completed-but-not-accepted plan is loaded automatically (survives page refresh)
 *  - An in-flight job resumes polling without the user clicking again
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { handleErrorToast, handleSuccessToast } from 'src/components/molecules';
import {
  InvestigationPlan,
  InvestigationStatus,
  InvestigationStep,
  PolicyReference,
  TargetedQuestion,
} from 'src/components/organisms/Investigation';
import {
  InvestigationPlanDocumentDocument,
  InvestigationPlanDocumentQuery,
  InvestigationPlanDocumentQueryVariables,
  InvestigationPlanJobStatusDocument,
  InvestigationPlanJobStatusQuery,
  InvestigationPlanJobStatusQueryVariables,
  InvestigationPlanStatus,
  InvestigationStepStatus,
  useAcceptInvestigationPlanMutation,
  useGenerateAndSaveInvestigationPlanMutation,
  useInvestigationPlanJobStatusQuery,
  useUpdateInvestigationChecklistMutation,
  useUpdateInvestigationPlanMutation,
  useUpdateInvestigationStepMutation,
} from 'src/graphql/generated';
import { fetcher } from 'src/utils/fetch';
import { InvestigationPlanApi } from './investigationApi';

const POLL_INTERVAL_MS = 3000;

function mapPlanJson(
  raw: Record<string, unknown>,
  caseId: string,
  docId: string,
  version: number,
): InvestigationPlan {
  const planOfAction =
    (raw.planOfAction as Array<{
      step: number;
      action: string;
      rationale: string;
      evidenceToGather: string[];
      relatedPolicyIds: string[];
    }>) ?? [];

  const persistedSteps =
    (raw.steps as Array<{ id: string; status: InvestigationStep['status'] }>) ??
    [];
  const statusById = new Map(persistedSteps.map((s) => [s.id, s.status]));

  const steps: InvestigationStep[] = planOfAction.map((s) => ({
    id: String(s.step),
    order: s.step,
    title: s.action,
    description: s.rationale,
    status: statusById.get(String(s.step)) ?? 'pending',
  }));

  const offenderInterviews =
    (raw.offenderInterviews as Array<{
      offenderRef: string;
      name: string;
      email?: string | null;
      witnessInvite?: { subject: string; body: string };
      questions: Array<{ question: string }>;
    }>) ?? [];

  const reporterInterview = raw.reporterInterview as {
    witnessInvite?: { subject: string; body: string };
    questions: Array<{ question: string }>;
  } | null;

  const targetedQuestions: TargetedQuestion[] = [
    ...offenderInterviews.map((oi) => ({
      id: oi.offenderRef,
      stakeholderType: 'SUBJECT' as const,
      stakeholderName: oi.name,
      questions: oi.questions.map((q) => q.question),
    })),
    ...(reporterInterview
      ? [
          {
            id: 'reporter',
            stakeholderType: 'ANONYMOUS_REPORTER' as const,
            questions: reporterInterview.questions.map((q) => q.question),
          },
        ]
      : []),
  ];

  const relevantPolicies =
    (raw.relevantPolicies as Array<{
      policyId: string;
      title: string;
      whyRelevant: string;
    }>) ?? [];

  const policies: PolicyReference[] = relevantPolicies.map((p) => ({
    policyId: p.policyId,
    title: p.title,
    relevance: p.whyRelevant,
  }));

  const isAccepted = Boolean(raw.acceptedAt);

  const firstInvite = offenderInterviews[0]?.witnessInvite;
  const caseRef = String(raw.caseRef ?? '');
  const scheduling =
    offenderInterviews.length > 0
      ? {
          stepId: '1',
          witnesses: offenderInterviews.map((oi) => ({
            name: oi.name,
            email: oi.email ?? undefined,
          })),
          subject:
            firstInvite?.subject ?? `Confidential Interview – Case ${caseRef}`,
          message:
            firstInvite?.body ??
            `This is a confidential interview request regarding Case ${caseRef}. Please treat this communication with discretion.`,
        }
      : undefined;

  const nextBestAction =
    offenderInterviews.length > 0
      ? {
          stepId: '1',
          title: 'Schedule Witness Interviews',
          description: `Schedule confidential interviews with ${offenderInterviews.map((oi) => oi.name).join(', ')}.`,
          actionType: 'schedule' as const,
        }
      : undefined;

  return {
    id: docId,
    caseId,
    caseDisplayId: caseRef,
    status: isAccepted
      ? InvestigationStatus.ACCEPTED
      : InvestigationStatus.READY,
    version,
    steps,
    checklist: typeof raw.checklist === 'string' ? raw.checklist : undefined,
    policies,
    targetedQuestions,
    scheduling,
    nextBestAction,
  };
}

const generatingPlaceholder = (
  caseId: string,
  caseDisplayId: string,
): InvestigationPlan => ({
  id: '',
  caseId,
  caseDisplayId,
  status: InvestigationStatus.GENERATING,
  steps: [],
  policies: [],
  targetedQuestions: [],
});

export const useRealInvestigationPlan = (
  caseId: string,
  caseDisplayId: string,
  enabled: boolean,
): InvestigationPlanApi => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAccepted, setIsAccepted] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [plan, setPlan] = useState<InvestigationPlan | null>(null);
  const completedRef = useRef(false);
  const planDocumentIdRef = useRef<string | null>(null);
  const rawPlanRef = useRef<Record<string, unknown> | null>(null);

  const generateMutation = useGenerateAndSaveInvestigationPlanMutation();
  const acceptMutation = useAcceptInvestigationPlanMutation();
  const updateStepMutation = useUpdateInvestigationStepMutation();
  const updateChecklistMutation = useUpdateInvestigationChecklistMutation();
  const updatePlanMutation = useUpdateInvestigationPlanMutation();

  const loadPlanDocument = useCallback(async () => {
    try {
      const docData = await fetcher<
        InvestigationPlanDocumentQuery,
        InvestigationPlanDocumentQueryVariables
      >(InvestigationPlanDocumentDocument, { caseId })();

      const doc = docData?.investigationPlanDocument;
      if (!doc) {
        setPlan((prev) =>
          prev ? { ...prev, status: InvestigationStatus.FAILED } : prev,
        );
        handleErrorToast({
          toastMessage: 'Investigation plan not found',
          toastDescription:
            'The plan document could not be retrieved. Please try generating again.',
        });
        return;
      }

      planDocumentIdRef.current = doc.id;
      const raw = JSON.parse(doc.value) as Record<string, unknown>;
      rawPlanRef.current = raw;
      setPlan(mapPlanJson(raw, caseId, doc.id, doc.version));
      handleSuccessToast({
        toastMessage: 'Investigation plan ready',
      });
    } catch {
      setPlan((prev) =>
        prev ? { ...prev, status: InvestigationStatus.FAILED } : prev,
      );
      handleErrorToast({
        toastMessage: 'Could not load investigation plan',
        toastDescription:
          'The plan was generated but could not be read. Please reload.',
      });
    }
  }, [caseId]);

  // On mount: check if there is already a completed or in-flight job so the
  // user can return to a generated plan after closing the modal or refreshing.
  useEffect(() => {
    if (!caseId || !enabled) return;
    fetcher<
      InvestigationPlanJobStatusQuery,
      InvestigationPlanJobStatusQueryVariables
    >(InvestigationPlanJobStatusDocument, { caseId })()
      .then((data) => {
        const status = data?.investigationPlanJobStatus?.status;
        if (status === InvestigationPlanStatus.Completed) {
          completedRef.current = true;
          loadPlanDocument();
        } else if (
          status === InvestigationPlanStatus.Pending ||
          status === InvestigationPlanStatus.InProgress
        ) {
          setPlan(generatingPlaceholder(caseId, caseDisplayId));
          setIsPolling(true);
        }
      })
      .catch(() => {});
    // Intentionally run only on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useInvestigationPlanJobStatusQuery(
    { caseId },
    {
      enabled: enabled && isPolling && Boolean(caseId),
      refetchInterval: (data) => {
        const status = data?.investigationPlanJobStatus?.status;
        return status === InvestigationPlanStatus.Pending ||
          status === InvestigationPlanStatus.InProgress
          ? POLL_INTERVAL_MS
          : false;
      },
      refetchIntervalInBackground: false,
      onSuccess: async (data) => {
        const status = data?.investigationPlanJobStatus?.status;
        if (
          status === InvestigationPlanStatus.Completed &&
          !completedRef.current
        ) {
          completedRef.current = true;
          setIsPolling(false);
          await loadPlanDocument();
        } else if (status === InvestigationPlanStatus.Failed) {
          setIsPolling(false);
          setPlan((prev) =>
            prev ? { ...prev, status: InvestigationStatus.FAILED } : prev,
          );
          handleErrorToast({
            toastMessage: 'Investigation plan generation failed',
            toastDescription: 'Something went wrong. Please try again.',
          });
        }
      },
    },
  );

  const generatePlan = useCallback(async () => {
    // If a plan is already loaded (ready but not yet accepted), just re-open
    // the modal rather than triggering a new generation.
    if (plan?.status === InvestigationStatus.READY) {
      setIsModalOpen(true);
      return;
    }

    if (isPolling) return;
    completedRef.current = false;
    planDocumentIdRef.current = null;
    rawPlanRef.current = null;

    setPlan(generatingPlaceholder(caseId, caseDisplayId));
    setIsModalOpen(true);

    try {
      await generateMutation.mutateAsync({ caseId });
      setIsPolling(true);
      handleSuccessToast({
        toastMessage: 'Investigation plan generation started',
      });
    } catch (error) {
      setPlan((prev) =>
        prev ? { ...prev, status: InvestigationStatus.FAILED } : prev,
      );
      handleErrorToast({
        toastMessage: 'Could not start investigation plan',
        toastDescription:
          error instanceof Error ? error.message : 'Please try again.',
      });
    }
  }, [caseId, caseDisplayId, generateMutation, isPolling, plan?.status]);

  const regeneratePlan = useCallback(() => {
    // Force a new generation regardless of current plan state.
    completedRef.current = false;
    planDocumentIdRef.current = null;
    rawPlanRef.current = null;
    setPlan(generatingPlaceholder(caseId, caseDisplayId));
    setIsModalOpen(true);

    generateMutation
      .mutateAsync({ caseId })
      .then(() => setIsPolling(true))
      .catch((error) => {
        setPlan((prev) =>
          prev ? { ...prev, status: InvestigationStatus.FAILED } : prev,
        );
        handleErrorToast({
          toastMessage: 'Could not regenerate investigation plan',
          toastDescription:
            error instanceof Error ? error.message : 'Please try again.',
        });
      });
  }, [caseId, caseDisplayId, generateMutation]);

  const acceptPlan = useCallback(async () => {
    const planDocumentId = planDocumentIdRef.current;
    if (!planDocumentId) {
      handleErrorToast({
        toastMessage: 'Could not accept investigation plan',
        toastDescription: 'Plan document not yet loaded. Please try again.',
      });
      return;
    }
    try {
      await acceptMutation.mutateAsync({ caseId, planDocumentId });
      setIsAccepted(true);
      setIsModalOpen(false);
      setPlan((prev) =>
        prev ? { ...prev, status: InvestigationStatus.ACCEPTED } : prev,
      );
    } catch (error) {
      handleErrorToast({
        toastMessage: 'Could not accept investigation plan',
        toastDescription:
          error instanceof Error ? error.message : 'Please try again.',
      });
    }
  }, [acceptMutation, caseId]);

  const updateStepStatus = useCallback(
    async (stepId: string, status: InvestigationStep['status']) => {
      const planDocumentId = planDocumentIdRef.current;
      if (!planDocumentId || !status) return;
      try {
        const stepStatusMap: Record<
          NonNullable<InvestigationStep['status']>,
          InvestigationStepStatus
        > = {
          pending: InvestigationStepStatus.Pending,
          in_progress: InvestigationStepStatus.InProgress,
          complete: InvestigationStepStatus.Complete,
          skipped: InvestigationStepStatus.Skipped,
        };
        await updateStepMutation.mutateAsync({
          caseId,
          planDocumentId,
          stepId,
          status: stepStatusMap[status],
        });
        // Refresh to pick up the new server version so subsequent plan edits
        // don't submit a stale submittedVersion and receive a false conflict.
        // Uses a separate fetch path to avoid loadPlanDocument's FAILED-state
        // side effect when the refresh itself fails.
        try {
          const docData = await fetcher<
            InvestigationPlanDocumentQuery,
            InvestigationPlanDocumentQueryVariables
          >(InvestigationPlanDocumentDocument, { caseId })();
          const doc = docData?.investigationPlanDocument;
          if (doc) {
            planDocumentIdRef.current = doc.id;
            const raw = JSON.parse(doc.value) as Record<string, unknown>;
            rawPlanRef.current = raw;
            setPlan(mapPlanJson(raw, caseId, doc.id, doc.version));
          }
        } catch {
          handleErrorToast({
            toastMessage: 'Could not refresh plan',
            toastDescription:
              'Step was saved. Reload the page before editing the plan.',
          });
        }
      } catch (error) {
        handleErrorToast({
          toastMessage: 'Could not update step',
          toastDescription:
            error instanceof Error ? error.message : 'Please try again.',
        });
      }
    },
    [caseId, updateStepMutation],
  );

  const updateChecklist = useCallback(
    (updatedMarkdown: string) => {
      const planDocumentId = planDocumentIdRef.current;
      if (!planDocumentId) return;
      const previousChecklist = plan?.checklist;
      setPlan((prev) =>
        prev ? { ...prev, checklist: updatedMarkdown } : prev,
      );
      updateChecklistMutation
        .mutateAsync({ caseId, planDocumentId, checklist: updatedMarkdown })
        .catch(() => {
          setPlan((prev) =>
            prev ? { ...prev, checklist: previousChecklist } : prev,
          );
          handleErrorToast({
            toastMessage: 'Could not save checklist',
            toastDescription: 'Please try again.',
          });
        });
    },
    [caseId, plan?.checklist, updateChecklistMutation],
  );

  const updatePlanContent = useCallback(
    async (updatedPlan: InvestigationPlan) => {
      const planDocumentId = planDocumentIdRef.current;
      const raw = rawPlanRef.current;
      const submittedVersion = updatedPlan.version;
      if (!planDocumentId || !raw || submittedVersion === undefined) {
        handleErrorToast({
          toastMessage: 'Could not save plan',
          toastDescription: 'Plan document not yet loaded. Please try again.',
        });
        return;
      }

      // Patch only the user-editable fields into the original raw JSON so
      // backend-owned fields (assessment, offenderInterviews, etc.) are preserved.
      const patched: Record<string, unknown> = {
        ...raw,
        planOfAction: updatedPlan.steps.map((s) => ({
          step: s.order,
          action: s.title,
          rationale: s.description,
          evidenceToGather:
            (raw.planOfAction as Array<{ evidenceToGather?: string[] }>)?.[
              s.order - 1
            ]?.evidenceToGather ?? [],
          relatedPolicyIds:
            (raw.planOfAction as Array<{ relatedPolicyIds?: string[] }>)?.[
              s.order - 1
            ]?.relatedPolicyIds ?? [],
        })),
        checklist: updatedPlan.checklist ?? raw.checklist ?? '',
      };

      try {
        const result = await updatePlanMutation.mutateAsync({
          caseId,
          planDocumentId,
          plan: JSON.stringify(patched),
          submittedVersion,
        });

        if (result.updateInvestigationPlan.conflict) {
          handleErrorToast({
            toastMessage: 'Could not save plan',
            toastDescription:
              'The plan was updated by someone else. Please reload and try again.',
          });
          throw new Error('conflict');
        }

        rawPlanRef.current = patched;
        const newVersion =
          result.updateInvestigationPlan.version ?? submittedVersion + 1;
        setPlan({ ...updatedPlan, version: newVersion });
      } catch (error) {
        if ((error as Error)?.message !== 'conflict') {
          handleErrorToast({
            toastMessage: 'Could not save plan',
            toastDescription:
              error instanceof Error ? error.message : 'Please try again.',
          });
        }
        throw error;
      }
    },
    [caseId, updatePlanMutation],
  );

  return {
    plan,
    isModalOpen,
    setIsModalOpen,
    generatePlan,
    acceptPlan,
    regeneratePlan,
    updateStepStatus,
    updateChecklist,
    updatePlanContent,
    isInvestigationActive:
      isAccepted || plan?.status === InvestigationStatus.ACCEPTED,
  };
};
